import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { toast } from "react-hot-toast";
import { getPlanDisplayName } from "../../services/razorpayService";

const pickFirstNumber = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return null;
};

const normalizeContactLimit = (raw) => {
  const source = Array.isArray(raw?.data) ? raw.data[0] || {} : raw?.data || {};
  const payload = source?.contact_limit || source;

  const added = pickFirstNumber(payload, [
    "friend_request_send_used",
    "added",
    "added_contact",
    "added_contacts",
    "used",
    "used_contacts",
    "total_added",
  ]);

  const left = pickFirstNumber(payload, [
    "friend_request_send_remaining",
    "left",
    "left_contact",
    "left_contacts",
    "remaining",
    "remaining_contacts",
    "available",
  ]);

  const limit = pickFirstNumber(payload, [
    "friend_request_send_total_limit",
    "limit",
    "contact_limit",
    "total_limit",
    "max_contacts",
    "allowed_contacts",
  ]);

  const usagePercentage = pickFirstNumber(payload, [
    "friend_request_send_usage_percentage",
    "usage_percentage",
    "usagePercent",
    "percent_used",
  ]);

  const pendingSent = pickFirstNumber(payload, [
    "friend_request_pending_sent",
    "pending_sent",
    "pending",
  ]);

  const planType = payload?.plan_type || payload?.plan || "-";

  const calculatedLimit = limit != null ? limit : added != null && left != null ? added + left : null;

  return {
    added,
    left,
    limit: calculatedLimit,
    usagePercentage,
    pendingSent,
    planType,
    raw: payload,
  };
};

export const ContactLimitPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    added: null,
    left: null,
    limit: null,
    usagePercentage: null,
    pendingSent: null,
    planType: "-",
    raw: null,
  });

  useEffect(() => {
    let mounted = true;

    const fetchContactLimit = async () => {
      try {
        setLoading(true);
        const token = Cookies.get("auth_token");

        if (!token) {
          toast.error("Session expired. Please log in again.");
          window.location.href = "/login";
          return;
        }

        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user_contact_limitation`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });

        const json = await res.json();

        if (json?.status === "1") {
          const parsed = normalizeContactLimit(json);
          if (mounted) setData(parsed);
        } else {
          toast.error(json?.message || "Failed to fetch contact limitation");
          if (mounted) {
            setData({
              added: null,
              left: null,
              limit: null,
              usagePercentage: null,
              pendingSent: null,
              planType: "-",
              raw: null,
            });
          }
        }
      } catch (err) {
        toast.error(err?.message || "Failed to fetch contact limitation");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchContactLimit();

    return () => {
      mounted = false;
    };
  }, []);

  const usedPercent = useMemo(() => {
    if (data.usagePercentage != null) {
      return Math.min(100, Math.max(0, Number(data.usagePercentage)));
    }
    if (data.limit == null || data.limit <= 0 || data.added == null) return 0;
    return Math.min(100, Math.max(0, Number(((data.added / data.limit) * 100).toFixed(2))));
  }, [data.limit, data.added, data.usagePercentage]);

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        <div className="h-6 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="h-36 bg-gray-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-lg font-semibold text-[#2B1B5B]">Contact Limit</h2>

      <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#EEE8FF]">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm text-gray-600">Plan</div>
          <div className="text-sm font-semibold text-[#3F2A79]">{getPlanDisplayName(data.planType) || data.planType}</div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-[#F6F2FF] p-4">
            <div className="text-xs text-[#6A5A92]">Added Contacts</div>
            <div className="mt-1 text-2xl font-bold text-[#3F2A79]">{data.added ?? "-"}</div>
          </div>
          <div className="rounded-xl bg-[#EEFDF3] p-4">
            <div className="text-xs text-[#2B6C47]">Left Contacts</div>
            <div className="mt-1 text-2xl font-bold text-[#1E7A4D]">{data.left ?? "-"}</div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Usage</span>
            <span className="font-semibold text-[#4B3A7B]">{usedPercent}%</span>
          </div>
          <div className="mt-2 w-full h-3 bg-[#F1ECFF] rounded-full overflow-hidden">
            <div className="h-3 bg-[#6C45FF]" style={{ width: `${usedPercent}%` }} aria-hidden />
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Limit: {data.limit ?? "-"}
          </div>
          <div className="mt-1 text-xs text-gray-500">
            Pending Sent: {data.pendingSent ?? "-"}
          </div>
        </div>
      </div>

      {data.added == null && data.left == null && data.limit == null && data.pendingSent == null ? (
        <div className="text-sm text-gray-600">Contact limit information is currently unavailable.</div>
      ) : null}
    </div>
  );
};

export default ContactLimitPage;
